<template>
  <component
    :is="specializedErrorComponent"
    v-if="specializedErrorComponent"
    :home-url="homeUrl"
  />
  <v-container v-else-if="error" class="pa-md-12">
    <v-empty-state v-if="error && error.deepBoxError === true">
      <template v-if="error.img || error.imgComponent || error.icon" #media>
        <component :is="error.imgComponent" v-if="error.imgComponent" />
        <v-img v-else-if="error.img" v-bind="error.img" />
        <v-avatar
          v-else-if="error.icon"
          :color="error.icon.color"
          size="88"
          variant="tonal"
        >
          <v-icon size="40" v-bind="error.icon" />
        </v-avatar>
      </template>
      <template v-if="error.title" #headline>
        <div v-if="error.title" class="text-headline-large">
          <span v-if="te(error.title)">
            {{ t(error.title) }}
          </span>
          <span v-else>
            {{ error.title }}
          </span>
        </div>
      </template>
      <template
        v-if="error.description || (!error.hideStatusCode && error.statusCode)"
        #title
      >
        <p
          v-if="error.description"
          class="mt-3"
          v-html="getSanitizedHtml(t(error.description))"
        ></p>
        <div v-else-if="!error.hideStatusCode && error.statusCode" class="mt-6">
          {{ t('error.code') + ': ' + error.statusCode }}
        </div>
      </template>

      <template v-if="error.message" #text>
        <div
          v-if="te(error.message)"
          v-html="getSanitizedHtml(t(error.message))"
        ></div>
        <div v-else v-html="getSanitizedHtml(error.message)"></div>
      </template>

      <template #actions>
        <VBtnPrimary
          v-if="error.btn"
          v-bind="error.btn"
          :href="
            'onClick' in error.btn ? undefined : (error.btn.href ?? homeUrl)
          "
          :text="error.btn.text || t('error.home')"
        />
        <VBtnPrimary
          v-else
          color="primary"
          :text="t('error.home')"
          :to="homeUrl"
        />
      </template>
    </v-empty-state>
  </v-container>
</template>

<script setup lang="ts">
import { DeepBoxError } from '@/models'
import { DEEP_BOX_ERROR_CODES } from '@/types/error.ts'
import { getSanitizedHtml } from '@deepcloud/deep-ui-utils/sanitize'
import { useOrganization } from '@/composables/organization/organization'
import ContainerErrorElevatedAuthRequired from '@/components/containers/ContainerErrorElevatedAuthRequired.vue'
import type { DeepBoxErrorCode } from '@/types/error.ts'
import type { UserMeOrganization } from '@deepcloud/api-deep-admin'

// errors with a fully custom, dedicated presentation instead of the generic layout below
const SPECIALIZED_ERROR_COMPONENTS: Partial<
  Record<DeepBoxErrorCode, Component>
> = {
  [DEEP_BOX_ERROR_CODES.ELEVATED_AUTH_REQUIRED]:
    ContainerErrorElevatedAuthRequired,
}

const props = defineProps({
  error: {
    type: Object as PropType<DeepBoxError>,
    default: undefined,
  },
  organization: {
    type: Object as PropType<UserMeOrganization>,
    default: undefined,
  },
})

const { t, te } = useI18n()

const { lastOrgId } = useOrganization()

const specializedErrorComponent = computed(() =>
  props.error?.errorCode
    ? SPECIALIZED_ERROR_COMPONENTS[props.error.errorCode]
    : undefined,
)

const homeUrl = computed(() => {
  let href = '/'
  if (props.organization?.group_id) {
    href += props.organization.group_id || lastOrgId.value
  }

  return href
})
</script>
